import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IconQrcode, IconInfoCircle } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { QRCodeCanvas } from 'qrcode.react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { LangContext } from '@/context/LangProvider';
import { ECompanyThemeName, EProjectStatus } from '@/enums';
import DownloadQRCode from './DownloadQrCode';
import EmbedCodeGenerator from './EmbedCodeGenerator';
import { ShareLinkProps } from '@/types';

function ProjectShareLink({ singleProjectData }: ShareLinkProps) {
  const { currentTheme, themeName } = useTheme();
  const { lang } = useContext(LangContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const { project_id } = useParams();
  const [projectLink, setProjectLink] = useState('');

  const handleCopyClick = async () => {
    if (inputRef.current) {
      try {
        await navigator.clipboard.writeText(inputRef.current.value);
        toast.info(lang.get('msg.linkCopied'));
      } catch (err) {
        toast.error(lang.get('msg.linkNotCopied'));
      }
    }
  };

  useEffect(() => {
    const PROJECT_LINK = `${import.meta.env.VITE_ENVIRONMENT_CHAT}/${project_id}`;
    const PROJECT_LINK_EMPATHY = `${import.meta.env.VITE_ENVIRONMENT_CHAT_EMPATHY}/${project_id}`;

    const link = themeName === ECompanyThemeName.EMPATHY ? PROJECT_LINK_EMPATHY : PROJECT_LINK;

    setProjectLink(link);
  }, [themeName, project_id]);

  return (
    <div className="flex flex-col w-full gap-5">
      {/* REAL CHAT */}
      <div className="flex flex-col gap-2">
        <p className="text-base font-medium">Link</p>

        {singleProjectData?.status === EProjectStatus.READY && (
          <p className="text-sm flex items-center gap-1.5 font-medium text-red-600">
            <IconInfoCircle size={17} />
            {lang.get('msg.yourProjectIsntPublishedYetPleasePublishItToStartYourInterviews')}
          </p>
        )}

        {singleProjectData?.status === EProjectStatus.DRAFT && (
          <p className="text-sm flex items-center gap-1.5 font-medium text-red-600">
            <IconInfoCircle size={17} />
            {lang.get('msg.someFieldsStillNeedToBeCompletedForYourInterviewToBeReady')}
          </p>
        )}

        <div
          style={{ backgroundColor: currentTheme?.['secondary-background'] }}
          className="flex flex-col items-center justify-center w-full gap-6 p-4 rounded-md"
        >
          <div className="flex w-full">
            <Input
              type="text"
              ref={inputRef}
              readOnly
              defaultValue={projectLink}
              className="rounded-tr-none rounded-br-none"
            />
            <Button
              variant="default"
              onClick={handleCopyClick}
              className="border-none rounded-tl-none rounded-bl-none"
            >
              Copy link
            </Button>
          </div>
          <div className="flex justify-center w-full gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="default" className="flex items-center gap-1 px-6" type="button">
                  <IconQrcode size={20} />
                  {lang.get('msg.QRCode')}
                </Button>
              </DialogTrigger>
              <DialogContent className="z-[101]">
                <DialogHeader>
                  <DialogTitle className="mb-1">{lang.get('msg.QRCode')}</DialogTitle>
                  <div className="text-dark-text">
                    <p className="text-sm">
                      {themeName === ECompanyThemeName.EMPATHY
                        ? lang.get('msg.scanQRCodeToGetTellet_empathy')
                        : lang.get('msg.scanQRCodeToGetTellet')}
                    </p>
                    <div className="flex items-center justify-center w-full my-4">
                      <QRCodeCanvas
                        id="qrElement"
                        value={projectLink}
                        size={280}
                        bgColor="#ffffff"
                        fgColor={currentTheme?.primary}
                        level="M"
                        includeMargin
                      />
                    </div>
                  </div>
                  <Button variant="default" onClick={() => DownloadQRCode('qrElement')}>
                    {lang.get('msg.downloadQRCode')}
                  </Button>
                </DialogHeader>
              </DialogContent>
            </Dialog>
            <EmbedCodeGenerator
              projectLink={projectLink}
              projectInputMethods={singleProjectData?.type_of_interview?.filter(
                (m): m is string => !!m,
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectShareLink;
